import searchIcon from '@jetbrains/icons/search.svg'
import Avatar, {Size as AvatarSize} from '@jetbrains/ring-ui/components/avatar/avatar'
import List from '@jetbrains/ring-ui/components/list/list'
import type {ReactElement} from 'react'
import React, {useCallback, useMemo, useState} from 'react'

import {useFilter} from '../../../hooks/useFilter'
import type {RepositoryGroup} from '../../../services/pipelinesApi.types'
import {RepositoryGroupType} from '../../../services/pipelinesApi.types'

import styles from './RepositoryList.css'

import Input, {Size} from 'src/components/pages/PipelinesPages/components/common/Input'

type RepositoryListItem = {
  key: string
  label: ReactElement
  repositoryName: string
  repositoryUrl: string
  defaultBranch: string
  leftNodes: ReactElement
  rightNodes: ReactElement
  className: string
  hoverClassName: string
  level: number
  isPrivate: boolean
}

type Props = {
  onSelect: (
    url: string,
    name: string,
    branch: string,
    isPrivate: boolean,
    username?: string,
  ) => void
  repositoryGroups: ReadonlyArray<RepositoryGroup>
}

function RepositoryList({repositoryGroups, onSelect}: Props) {
  const [filterValue, setFilterValue] = useState<string>('')

  const data: ReadonlyArray<RepositoryListItem> = useMemo(() => {
    const filterActive = filterValue.trim().length > 0
    const result: RepositoryListItem[] = []
    repositoryGroups.forEach(({myOwner, myRepositories}) => {
      myRepositories.forEach(repo => {
        if (
          !filterActive ||
          `${myOwner.login}/${repo.name}`.toLowerCase().includes(filterValue.toLowerCase()) ||
          repo.htmlUrl.toLowerCase().includes(filterValue.toLowerCase())
        ) {
          result.push({
            key: `${myOwner.login}_${repo.name}`,
            label: (
              <span>
                {repo.name}
                {repo.private ? (
                  <span className={styles.labelPrivate}>{' \u00a0Private'}</span>
                ) : undefined}
              </span>
            ),
            repositoryName: repo.name,
            repositoryUrl: repo.htmlUrl,
            defaultBranch: repo.defaultBranch,
            leftNodes: (
              <Avatar
                className={styles.image}
                size={AvatarSize.Size20}
                alt={myOwner.login}
                title={myOwner.login}
                url={myOwner.avatarUrl} // TODO there is an issue with Content-Security-Policy
                username={myOwner.login}
              />
            ),
            rightNodes: <span className={styles.labelCreate}>{'Create'}</span>,
            className: styles.item,
            hoverClassName: styles.itemHover,
            level: 6, // 2 levels margin + 3 levels are reserved for the image + 1 level margin
            isPrivate: repo.private,
          })
        }
      })
    })
    return result
  }, [filterValue, repositoryGroups])

  const username = repositoryGroups.find(
    group => group.myGroupType === RepositoryGroupType.CURRENT_USER,
  )?.myOwner.login
  const onSelectItem = useCallback(
    (i: RepositoryListItem) => {
      onSelect(i.repositoryUrl, i.repositoryName, i.defaultBranch, i.isPrivate, username)
    },
    [onSelect, username],
  )

  const {
    resetActiveIndex,
    onFilterKeyPress,
    onFilterChange,
    onFilterSubmit,
    onFilterClear,
    activeIndex,
  } = useFilter<RepositoryListItem>({data, onSelect: onSelectItem, setFilterValue})

  return (
    <>
      <form onSubmit={onFilterSubmit}>
        <Input
          size={Size.L}
          onChange={onFilterChange}
          onClear={onFilterClear}
          onFocus={resetActiveIndex}
          onBlur={resetActiveIndex}
          onKeyDown={onFilterKeyPress}
          placeholder="Search by name, URL or owner"
          value={filterValue}
          autoComplete="off"
          icon={searchIcon}
        />
      </form>
      <List<RepositoryListItem>
        className={styles.list}
        data={data}
        onSelect={onSelectItem}
        shortcuts
        activeIndex={activeIndex}
      />
    </>
  )
}

export default RepositoryList
